import React, { useEffect, useState } from 'react'
import {
    Autocomplete, Box, Button,
    Collapse, Container, FormControl,
    Grid, IconButton, Paper,
    Stack, TextField, Typography
}                                     from '@mui/material'
import { Trans, useTranslation }      from 'react-i18next'
import { DateTime }                   from 'luxon'
import Iconify                        from '../ui/components/Iconify'
import Page                           from '../ui/components/Page'
import ShortFormControl               from '../ui/components/short-form-control'
import { useApi }                     from '../utils/api'
import { BaseBreadcrumbs }            from '../ui/components/BaseBreadcrumbs'
import palette                        from '../ui/theme/palette'
import Loader                         from '../ui/components/Loader'
import { useParams }                  from 'react-router-dom'
import { useSnackbar }                from 'notistack'
import Consts                         from '../Consts'

export default function SmsBroadcastPage () {
    const api = useApi()
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { serviceIds, from, to } = useParams()
    const [filterOpen, setFilterOpen] = useState(true)
    const [filter, setFilter] = useState({
        dateFrom: from ?? DateTime.now().toFormat('yyyy-MM-dd'),
        dateTo: to ?? DateTime.now().toFormat('yyyy-MM-dd'),
        selectedServices: []
    })
    const [shownFilter, setShownFilter] = useState({
        dateFrom: from ?? DateTime.now().toFormat('yyyy-MM-dd'),
        dateTo: to ?? DateTime.now().toFormat('yyyy-MM-dd'),
        selectedServices: [],
    })
    const [loading, setLoading] = useState(false)
    const [recipientDetails, setRecipientDetails] = useState('')
    const [services, setServices] = useState([])

    useEffect(() => {
        api.service.listByConnectionType(Consts.Service.connectionTypePremium).then(({data}) => {
            setServices(data)
            const selectedServices = (serviceIds ?? '').split(',')?.map(it => parseInt(it))?.map(serviceId => data.find(it => it.id === serviceId))?.filter(it => !!it) ?? []
            setFilter((prev) => ({
                ...prev,
                selectedServices: selectedServices
            }))
            setShownFilter((prev) => ({
                ...prev,
                selectedServices: selectedServices
            }))
        }).catch(e => {
            enqueueSnackbar(t('Failed to load services'), {
                variant: 'error'
            })
        })
    }, [])

    const onSearchSubmit = async (e) => {
        e?.preventDefault()
        // first update history token
        const urlPath = `/sms-broadcast/${filter.dateFrom}/${filter.dateTo}` + (filter.selectedServices.length > 0 ? `/${filter.selectedServices.map(it => it.id).join(',')}` : '')
        window.history.replaceState(null, t('Sms broadcast'), urlPath)
        // load data
        setLoading(() => true)
        const dateFrom = filter.dateFrom.replaceAll('-', '')
        const dateTo = filter.dateTo.replaceAll('-', '')
        const serviceIds = filter.selectedServices.map(it => it.id)

        const uniquePhoneNumbers = new Set()
        const messagesFromPremiumServices = []
        try {
            const count = 1000
            let page = 0
            let fetchedCount = 0
            do {
                const premiumMessages = await api.message.list(null, dateFrom, dateTo, serviceIds, true, Consts.Service.connectionTypePremium, '', page, count)
                fetchedCount = premiumMessages.data.length
                for (const message of premiumMessages.data) {
                    if (!message.telTo || uniquePhoneNumbers.has(message.telTo)) {
                        continue
                    }
                    uniquePhoneNumbers.add(message.telTo)
                    messagesFromPremiumServices.push(message)
                }
                page++
            } while (fetchedCount === count)
        } catch (e) {
            enqueueSnackbar(t('Failed to load messages'), {
                variant: 'error'
            })
        } finally {
            setShownFilter(() => ({ ...filter }))
            setLoading(() => false)
        }
        setRecipientDetails(messagesFromPremiumServices.map(m => `${m.telTo};${m.telFrom};${m.operatorShort}`).join('\n'))
    }

    const makeFilterTitle = () => {
        const reqDateFrom = (shownFilter.dateFrom ? DateTime.fromFormat(shownFilter.dateFrom, 'yyyy-MM-dd') : DateTime.now()).toFormat('dd.MM.yyyy')
        const reqDateTo = (shownFilter.dateTo ? DateTime.fromFormat(shownFilter.dateTo, 'yyyy-MM-dd') : DateTime.now()).toFormat('dd.MM.yyyy')
        const reqServices = shownFilter.selectedServices.length === 0 ? t('all services') : shownFilter.selectedServices.map(it => it.title).join(', ')
        return <Trans i18nKey={'smsBroadcastHighlight'} values={{ from: reqDateFrom, to: reqDateTo, services: reqServices }} />
    }

    return (
        <Page title={t('Sms broadcast')}>
            <Container component="main">
                <BaseBreadcrumbs key={'breadcrumb-sms-broadcast'}
                                 title={t('Sms broadcast')}
                                 items={[]}/>
                <Grid container>
                    <Grid item xs={12}>
                        <Stack direction={'row'} spacing={2} alignItems={'baseline'} mb={2}>
                            <Typography variant={'h6'}>{t('Filter')}</Typography>
                            <Typography variant="body1" sx={{ color: palette.text.disabled }}>
                                {makeFilterTitle()}
                            </Typography>
                            <Loader loading={loading} direction={'row'} size={'1.5em'} justifyContent={'start'} />
                            <Box flexGrow={1}/>
                            <IconButton onClick={(e) => setFilterOpen(prev => !prev)}>
                                <Iconify icon={filterOpen ? 'material-symbols:filter-alt-outline' : 'material-symbols:filter-alt'}/>
                            </IconButton>
                        </Stack>
                    </Grid>
                </Grid>
                <Collapse in={filterOpen}>
                    <Grid container component={'form'} onSubmit={onSearchSubmit} spacing={2} rowSpacing={2} direction={'row'} alignItems={'flex-end'} justifyContent={'flex-end'}>
                        <Grid item xs={6} sm={'auto'}>
                            <ShortFormControl title={t('From')} handleChange={() => e => setFilter(prev => ({ ...prev, dateFrom: e.target?.value }))} value={filter.dateFrom} name={'from'} inputProps={{ type: 'date' }} />
                        </Grid>
                        <Grid item xs={6} sm={'auto'}>
                            <ShortFormControl title={t('To')} handleChange={() => e => setFilter(prev => ({ ...prev, dateTo: e.target?.value }))} value={filter.dateTo} name={'to'} inputProps={{ type: 'date' }} />
                        </Grid>
                        <Grid item xs={12} sm={'auto'} sx={{ minWidth: { sm: '250px' } }}>
                            <Autocomplete
                                multiple
                                fullWidth
                                id="tags-services"
                                options={services}
                                getOptionLabel={(option) => option.title}
                                value={filter.selectedServices}
                                onChange={(e, newValue) => {
                                    setFilter((prev) => ({
                                        ...prev,
                                        selectedServices: newValue
                                    }))
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="standard"
                                        label={t('Premium services')}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={'auto'} sx={{ maxWidth: { sm: '200px' } }}>
                            <Button fullWidth type={'submit'} variant="outlined">{t('Confirm')}</Button>
                        </Grid>
                    </Grid>
                </Collapse>
                <Paper sx={{ mt: 2, p: 2 }}>
                    <Grid item xs={12}>
                        <FormControl variant="standard" fullWidth>
                            <TextField
                                id="recipient-details"
                                label={t('RecipientDetails')}
                                multiline
                                minRows={20}
                                value={recipientDetails}
                                onChange={(e) => setRecipientDetails(e.target.value)}
                                fullWidth
                                variant="outlined"
                                sx={{ width: '100%' }}/>
                        </FormControl>
                    </Grid>
                </Paper>
            </Container>
        </Page>
    )
}
